/**
 * PackagesSection.tsx
 *
 * Multi-package line editor for the Add Package modal — the package-side
 * analogue of PaymentProductsSection. Staff add one or more package templates,
 * override each price, and apply up to two discounts per package. Totals update
 * live and the combined net feeds back to the modal as the charge amount.
 *
 * Discount math is delegated to `computeLineTotal` (the exact function the
 * charge modal uses for product lines) via a ProductLine adapter, so packages
 * discount identically to products and match the server's net calculation.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2, PackageOpen } from 'lucide-react';
import {
  getPackageTemplates,
  type PackageTemplate,
  type PackageItem,
} from '@/services/packages.service';
import { listDiscounts, type PaymentDiscount } from '@/services/payments.service';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { computeLineTotal, type ProductLine } from '@/components/payments/PaymentProductsSection';
import { LineDiscountEditor } from '@/components/payments/LineDiscountEditor';
import { descriptionToText } from '@/lib/utils';

export interface PackageLine {
  packageId: number;
  name: string;
  description: string | null;
  /** Gross (pre-discount) price — defaults to the template price, editable. */
  price: number;
  items: PackageItem[];
  discount1Id: number | null;
  manualDiscount1: number;
  discount2Id: number | null;
  manualDiscount2: number;
}

interface PackagesSectionProps {
  onChange: (lines: PackageLine[]) => void;
}

/** A line as edited inside this section. `priceText` is the raw text in the
 *  price box so a partially typed value like "150." survives the re-render;
 *  the parsed `price` is what leaves the component. */
interface EditablePackageLine extends PackageLine {
  priceText: string;
}

function toPriceText(price: number): string {
  return price === 0 ? '' : price.toFixed(2);
}

function toPackageLine({ priceText: _priceText, ...line }: EditablePackageLine): PackageLine {
  return line;
}

function fmtCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

/** Adapt a package line into the ProductLine shape (quantity 1, no tax) so the
 *  shared `computeLineTotal` computes the post-discount total. */
function toProductLine(line: PackageLine): ProductLine {
  return {
    itemId: line.packageId,
    name: line.name,
    description: line.description ?? '',
    unit: '',
    price: line.price,
    quantity: 1,
    taxable: false,
    salesTax: 0,
    discount1Id: line.discount1Id,
    manualDiscount1: line.manualDiscount1,
    discount2Id: line.discount2Id,
    manualDiscount2: line.manualDiscount2,
    productType: 0,
  };
}

/** Post-discount total for one package line. */
export function computePackageLineTotal(line: PackageLine, discounts: PaymentDiscount[]): number {
  return computeLineTotal(toProductLine(line), discounts);
}

export function PackagesSection({ onChange }: PackagesSectionProps) {
  const [lines, setLines] = useState<EditablePackageLine[]>([]);

  const { data: templates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: ['package-templates'],
    queryFn: getPackageTemplates,
    staleTime: 5 * 60_000,
  });

  const { data: discounts = [] } = useQuery({
    queryKey: ['payment-discounts'],
    queryFn: () => listDiscounts(),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    onChange(lines.map(toPackageLine));
  }, [lines, onChange]);

  const comboboxOptions = useMemo<ComboboxOption<number>[]>(
    () =>
      templates
        .filter((t) => t.active)
        .map((t) => ({ value: t.packageId, label: t.name, meta: fmtCurrency(t.price) })),
    [templates],
  );

  const addPackage = (template: PackageTemplate) => {
    setLines((prev) => [
      ...prev,
      {
        packageId: template.packageId,
        name: template.name,
        description: template.description,
        price: template.price,
        priceText: toPriceText(template.price),
        items: template.items ?? [],
        discount1Id: null,
        manualDiscount1: 0,
        discount2Id: null,
        manualDiscount2: 0,
      },
    ]);
  };

  const updateLine = (index: number, patch: Partial<EditablePackageLine>) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const totals = useMemo(() => {
    let gross = 0;
    let grand = 0;
    for (const line of lines) {
      gross += line.price;
      grand += computePackageLineTotal(line, discounts);
    }
    return {
      gross: Math.round(gross * 100) / 100,
      discount: Math.round((gross - grand) * 100) / 100,
      grand: Math.round(grand * 100) / 100,
    };
  }, [lines, discounts]);

  return (
    <div className="space-y-3">
      <SearchableCombobox<number>
        label="Package"
        mode="multi-add"
        options={comboboxOptions}
        loading={loadingTemplates}
        searchByLabelOnly
        closeOnAdd
        placeholder="Search packages..."
        emptyMessage="No packages match your search"
        onAdd={(opt) => {
          const template = templates.find((t) => t.packageId === opt.value);
          if (template) addPackage(template);
        }}
      />

      {lines.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-md border border-slate-200 py-8">
          <PackageOpen className="h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-500">No packages added</p>
          <p className="text-xs text-slate-400">Search above to add packages to this purchase</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lines.map((line, index) => {
            const net = computePackageLineTotal(line, discounts);
            return (
              <div key={`pkg-${index}-${line.packageId}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-900">{line.name}</div>
                    {descriptionToText(line.description) && <div className="text-xs text-slate-500">{descriptionToText(line.description)}</div>}
                    {line.items.length > 0 && (
                      <div className="mt-2 space-y-0.5">
                        {line.items.map((item) => {
                          const expLabel =
                            (item.expireMonths ?? 0) > 0
                              ? `${item.expireMonths} mo`
                              : (item.expireDays ?? 0) > 0
                                ? `${item.expireDays} days`
                                : null;
                          return (
                            <div key={item.packageItemId} className="flex items-baseline gap-2 text-xs text-slate-700">
                              <span className="w-6 text-right font-semibold tabular-nums">{item.quantity}</span>
                              <span>{item.itemName}</span>
                              {expLabel && (
                                <span className="text-slate-400">expires in {expLabel}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-400">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={line.priceText}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                            updateLine(index, { priceText: v, price: Number(v) || 0 });
                          }
                        }}
                        onBlur={() => updateLine(index, { priceText: toPriceText(line.price) })}
                        placeholder="0.00"
                        className="w-24 rounded border border-slate-200 px-2 py-1 text-right text-lg font-bold text-slate-900 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => removeLine(index)}
                      title="Remove package"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                  <LineDiscountEditor
                    value={{
                      discount1Id: line.discount1Id,
                      manualDiscount1: line.manualDiscount1,
                      discount2Id: line.discount2Id,
                      manualDiscount2: line.manualDiscount2,
                    }}
                    discounts={discounts}
                    baseAmount={line.price}
                    onChange={(patch) => updateLine(index, patch)}
                  />
                  <div className="shrink-0 text-right">
                    {net !== line.price && (
                      <span className="mr-2 text-xs text-slate-400 line-through">{fmtCurrency(line.price)}</span>
                    )}
                    <span className="font-mono text-sm font-semibold text-slate-900">{fmtCurrency(net)}</span>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span className="font-mono">{fmtCurrency(totals.gross)}</span>
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Discount:</span>
                <span className="font-mono">-{fmtCurrency(totals.discount)}</span>
              </div>
            )}
            <div className="mt-1 flex justify-between border-t border-slate-300 pt-1 font-bold">
              <span>Packages Total:</span>
              <span className="font-mono">{fmtCurrency(totals.grand)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
